/**
 * DealTagChipEditor — compact inline chip editor for a single deal_id.
 *
 * - Renders assigned tag chips.
 * - "+ Add" button opens a dropdown to toggle tags on/off or create a new one.
 * - Debounces PUT: fires immediately on toggle for snappy feel.
 *
 * Props:
 *   token       — auth token
 *   dealId      — string (required; empty → renders "-" and disabled)
 *   allTags     — array of {tag_id, name} available in the workspace
 *   value       — array of tag_id strings currently applied
 *   onChange    — (nextTagIds) => void; called after successful PUT
 *   onTagsChanged — () => void; optional, called when the tag catalog changed
 *                   (e.g. a new tag was created inline)
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { setDealTags, createTag, tagChipStyle } from "./dealTagsApi";

const DealTagChipEditor = ({ token, dealId, allTags = [], value = [], onChange, onTagsChanged }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const menuRef = useRef(null);

  const tagsById = useMemo(() => {
    const m = new Map();
    allTags.forEach((t) => m.set(t.tag_id, t));
    return m;
  }, [allTags]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTags
      .filter((t) => !q || t.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allTags, query]);

  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q && allTags.some((t) => t.name.toLowerCase() === q);
  }, [query, allTags]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const commit = async (nextIds) => {
    if (!dealId) return;
    setSaving(true);
    try {
      await setDealTags(token, dealId, nextIds);
      onChange && onChange(nextIds);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save tags");
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId) => {
    const nextIds = value.includes(tagId)
      ? value.filter((t) => t !== tagId)
      : [...value, tagId];
    commit(nextIds);
  };

  const removeTag = (tagId) => {
    commit(value.filter((t) => t !== tagId));
  };

  const createInline = async () => {
    const name = query.trim();
    if (!name) return;
    try {
      const created = await createTag(token, name);
      onTagsChanged && onTagsChanged();
      setQuery("");
      commit([...value, created.tag_id]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tag");
    }
  };

  if (!dealId) {
    return <span className="text-[#ebebeb]/30 text-xs font-mono">-</span>;
  }

  return (
    <div className="relative inline-flex items-center gap-1 flex-wrap" data-testid={`deal-tag-editor-${dealId}`}>
      {value.map((tid) => {
        const t = tagsById.get(tid);
        if (!t) return null;
        return (
          <span
            key={tid}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono"
            style={tagChipStyle(tid)}
            data-testid={`chip-${tid}`}
          >
            {t.name}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tid); }}
              className="opacity-60 hover:opacity-100"
              aria-label={`Remove ${t.name}`}
              disabled={saving}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-dashed border-white/15 text-[10px] font-mono text-[#ebebeb]/50 hover:text-[#ebebeb]/80 hover:border-white/30"
        data-testid={`add-tag-btn-${dealId}`}
      >
        <Plus className="w-3 h-3" />
        {value.length === 0 ? "Tag" : ""}
      </button>

      {open && (
        <div
          ref={menuRef}
          className="absolute top-full left-0 mt-1 z-50 w-64 bg-[#1e2124] border border-[#2a2d31] rounded-lg shadow-xl py-2"
          data-testid={`tag-menu-${dealId}`}
        >
          <div className="px-2 pb-2 border-b border-[#2a2d31]">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search or create tag…"
              className="w-full bg-[#1a1c1f] border border-[#2a2d31] rounded px-2 py-1 text-xs font-mono text-[#ebebeb] focus:outline-none focus:border-[#ff3737]"
              data-testid={`tag-search-${dealId}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim() && !exactMatch) {
                  e.preventDefault();
                  createInline();
                }
              }}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && !query.trim() && (
              <div className="px-3 py-2 text-[11px] text-[#ebebeb]/40 font-mono">
                No tags yet. Type a name to create one.
              </div>
            )}
            {filtered.map((t) => {
              const on = value.includes(t.tag_id);
              return (
                <button
                  key={t.tag_id}
                  type="button"
                  onClick={() => toggleTag(t.tag_id)}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-mono text-[#ebebeb]/80 hover:bg-white/5"
                  data-testid={`tag-option-${t.tag_id}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: tagChipStyle(t.tag_id).color }}
                    />
                    {t.name}
                  </span>
                  {on && <Check className="w-3.5 h-3.5 text-[#ff3737]" />}
                </button>
              );
            })}
            {query.trim() && !exactMatch && (
              <button
                type="button"
                onClick={createInline}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-mono text-[#ff3737] hover:bg-white/5 border-t border-[#2a2d31] mt-1"
                data-testid={`tag-create-inline-${dealId}`}
              >
                <Plus className="w-3.5 h-3.5" /> Create "{query.trim()}"
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealTagChipEditor;
